/**
 * `ChannelConversationList` — the chat list of the IM tabs, styled like a
 * messenger's conversation list (avatar · name · last-message preview · time).
 * Newest first (server order; the client NEVER re-sorts). Read-only. Each row
 * opens the thread; the `peer:null` stash-miss bucket is rendered + labeled
 * honestly, never dropped (dropping is data loss in a monitoring surface).
 *
 * The peer's own messenger username (`peerName`) LEADS (operator flip
 * 2026-07-23 — this is a Telegram/WhatsApp screen, so it shows the name the
 * account carries there); the curated Lexiun contact name is the fallback, then
 * the raw peer. In the two-column layout the selected row is highlighted and,
 * when asked, the first conversation auto-opens so the pane is never blank.
 *
 * See `ai-docs/lexide-agent-channel-tabs-impl-plan.md` §5.2 +
 * `ai-docs/lexide-agent-im-tabs-options-plan.md`.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Search } from "lucide-react";

import { useChannelConversations } from "@/hooks/useChannels";
import type { ChannelConversation } from "@/api/lx-channels";
import { channelScopeToCaseId } from "@/lib/channel-scope";
import { buildCaseLabelIndex, resolveCaseLabel } from "@/components/team-of-agents/case-label";
import { useCases } from "@/hooks/useCases";
import { PeerAvatar, PeerTierBadge } from "@/components/agent/PeerAvatar";
import {
  getMessengerTheme,
  listStamp,
  stripInline,
} from "@/components/agent/messenger-theme";
import type { PeerContact } from "@/hooks/useContactPeerNames";
import { matchesSearch } from "@/lib/channel-search";
import { cn } from "@/lib/utils";

/**
 * The themed column every list state renders inside — full height, own
 * scroller below, channel panel colour.
 *
 * Defined at MODULE scope on purpose: a component declared inside the render
 * function is a new type on every render, so React would unmount and remount
 * this whole subtree on each keystroke — taking focus out of the search field.
 */
function ListShell({ panelClass, children }: { panelClass: string; children: ReactNode }) {
  return <div className={cn("flex h-full min-h-0 flex-col", panelClass)}>{children}</div>;
}

export function ChannelConversationList({
  channel,
  onSelect,
  resolvePeerName,
  selectedPeer,
  autoSelectFirst = false,
}: {
  channel: string;
  onSelect: (conversation: ChannelConversation) => void;
  /** Peer → curated contact identity (contacts join). Absent/`undefined` ⇒
   *  the row degrades to `peerName`, then the raw peer. */
  resolvePeerName?: (channel: string, peer: string | null | undefined) => PeerContact | undefined;
  /** The peer of the open conversation (two-column mode) to highlight.
   *  `undefined` = nothing selected; `null` = the null-peer bucket is open. */
  selectedPeer?: string | null;
  /** Two-column mode: open the first conversation so the pane is never blank. */
  autoSelectFirst?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { conversations, isLoading, error } = useChannelConversations(channel, {
    enabled: true,
  });
  const { data: caseList } = useCases();
  const caseIndex = useMemo(() => buildCaseLabelIndex(caseList), [caseList]);
  const theme = getMessengerTheme(channel);
  const [query, setQuery] = useState("");
  const now = Date.now();
  const stampLabels = { yesterday: t("agent.channels.thread.yesterday") };

  // Two-column: open the first conversation when nothing is selected yet, so
  // the conversation column is never blank (mirrors the email tab).
  useEffect(() => {
    if (autoSelectFirst && selectedPeer === undefined && conversations.length > 0) {
      onSelect(conversations[0]!);
    }
  }, [autoSelectFirst, selectedPeer, conversations, onSelect]);

  // EVERY state renders through the same themed shell (`ListShell`, defined at
  // module scope), because the column that hosts us is now `overflow-hidden` —
  // the list owns its own scrolling. A state that returned a bare div would
  // lose both the scroller (clipping a long error in a short pane) and the
  // channel's list-panel background.
  if (isLoading && conversations.length === 0) {
    return (
      <ListShell panelClass={theme.listPanelClass}>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto py-16 text-cream-500">
          <Loader2 size={20} className="animate-spin" />
        </div>
      </ListShell>
    );
  }

  if (error && conversations.length === 0) {
    return (
      <ListShell panelClass={theme.listPanelClass}>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div
            data-testid="channel-conversations-error"
            role="alert"
            className="m-3 rounded border border-red-200 bg-red-50 p-3 text-body-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {t("agent.channels.loadFailed", { message: error })}
          </div>
        </div>
      </ListShell>
    );
  }

  if (conversations.length === 0) {
    return (
      <ListShell panelClass={theme.listPanelClass}>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div
            data-testid="channel-conversations-empty"
            className="py-16 text-center text-body-sm text-cream-500"
          >
            {t("agent.channels.conversationsEmpty")}
          </div>
        </div>
      </ListShell>
    );
  }

  // Telegram/WhatsApp screen: the account's own messenger username leads
  // (operator 2026-07-23), then the curated Lexiun name, then the raw peer.
  // (The username is runtime-stamped; empty until the P1b stamp reaches a peer.)
  const labelOf = (c: ChannelConversation) =>
    c.peerName?.trim() || resolvePeerName?.(channel, c.peer)?.name || c.peer ||
    t("agent.channels.unknownSender");

  // Filters what the pane ALREADY holds — never a server query, so the field
  // cannot imply it searches history that was never loaded.
  const visible = query
    ? conversations.filter((c) => matchesSearch(query, [labelOf(c), c.lastText, c.peer]))
    : conversations;

  return (
    <ListShell panelClass={theme.listPanelClass}>
      {theme.listSearchClass && (
        // The LIST owns its scrolling (below), so the field simply sits above
        // it rather than relying on `sticky` inside someone else's scroller.
        <div className="shrink-0 px-3 py-2">
          <div className={cn("flex items-center gap-2 px-3 py-1.5", theme.listSearchClass)}>
            <Search size={15} aria-hidden className="shrink-0 text-cream-500" />
            <input
              type="search"
              data-testid="channel-conversations-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("agent.channels.searchPlaceholder")}
              aria-label={t("agent.channels.searchPlaceholder")}
              className="min-w-0 flex-1 bg-transparent text-body-sm text-coffee-900 outline-none placeholder:text-cream-500 dark:text-cream-100"
            />
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <div
          data-testid="channel-conversations-no-results"
          className="min-h-0 flex-1 overflow-y-auto py-10 text-center text-body-sm text-cream-500"
        >
          {t("agent.channels.searchNoResults")}
        </div>
      ) : (
        <ul
          data-testid="channel-conversations"
          className={cn("min-h-0 flex-1 overflow-y-auto", theme.listRowDividerClass)}
        >
          {visible.map((c, i) => {
            const contact = resolvePeerName?.(channel, c.peer);
            const label = labelOf(c);
            const caseChips = uniqueScopeLabels(
              c.scopes,
              caseIndex,
              t("agent.channels.generalScope"),
            );
            const isSelected = selectedPeer !== undefined && c.peer === selectedPeer;
            // Per the CHANNEL's own grammar: WhatsApp gets its four buckets,
            // Telegram keeps the two-way split it shipped with (see listStamp).
            const stamp = listStamp(c.lastTs, now, i18n.language, stampLabels, theme.listStamp);
            return (
              <li key={c.peer ?? `null-${i}`}>
                <button
                  type="button"
                  data-testid={
                    c.peer === null ? "channel-conversation-row-null" : "channel-conversation-row"
                  }
                  data-peer={c.peer ?? ""}
                  aria-current={isSelected || undefined}
                  onClick={() => onSelect(c)}
                  className={cn(
                    "flex w-full items-center gap-3 text-left transition-colors",
                    theme.listRowClass,
                    isSelected
                      ? "bg-cream-200/70 dark:bg-coffee-800"
                      : "hover:bg-cream-100/70 dark:hover:bg-coffee-900/60",
                  )}
                >
                  {/* Null-peer bucket is an aggregate of unresolved peers — a
                      neutral glyph, never a monogram that reads as one person. */}
                  <PeerAvatar label={c.peer === null ? "" : label} size={48} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span
                        className="min-w-0 flex-1 truncate text-body-sm font-semibold text-coffee-900 dark:text-cream-100"
                        title={c.peer ?? undefined}
                      >
                        {label}
                      </span>
                      {contact?.tier && <PeerTierBadge tier={contact.tier} />}
                      <span className="shrink-0 text-[11px] text-cream-500">{stamp}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <p className="min-w-0 flex-1 truncate text-body-sm text-coffee-600 dark:text-cream-400">
                        {stripInline(c.lastText)}
                      </p>
                      {caseChips.length > 0 && (
                        <span className="shrink-0 rounded bg-cream-200/70 px-1.5 py-0.5 text-[10px] text-coffee-600 dark:bg-coffee-800 dark:text-cream-400">
                          {caseChips.length > 1
                            ? `${caseChips[0]} +${caseChips.length - 1}`
                            : caseChips[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </ListShell>
  );
}

/** Resolve a peer's scope keys to distinct case labels (global → the "Geral"
 *  chip). Deduped + order-preserving so a peer that spans several cases shows
 *  each once, and a single-case peer shows one chip. */
function uniqueScopeLabels(
  scopes: string[],
  caseIndex: Map<string, string>,
  generalLabel: string,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const scope of scopes) {
    const caseId = channelScopeToCaseId(scope);
    const label = caseId ? resolveCaseLabel(caseId, caseIndex) ?? caseId : generalLabel;
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}
